/**
 * Microsoft Clarity Block
 *
 * Injection block that loads the Clarity analytics/heatmap script.
 * Site-wide: same project_id across all pages.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'clarity-script';

function MicrosoftClarityComponent(_props: BlockProps) {
  const projectId = useBlockToken('project_id', { defaultValue: '' });
  const trackingAllowed = useTrackingAllowed('analytics');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !projectId) return;

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.textContent = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${projectId}");`;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, projectId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'MicrosoftClarity',
  displayName: 'Microsoft Clarity',
  tokens: {
    project_id: {
      label: 'Project ID',
      type: 'text',
      description: 'Clarity project ID (alphanumeric string).',
      required: true,
    },
  },
};

export const microsoftClarityBlock: BlockDefinition = {
  id: 'microsoft-clarity',
  name: 'Microsoft Clarity',
  description: 'Heatmaps, session recordings, and behavior analytics via Microsoft Clarity.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: MicrosoftClarityComponent,
  contentSchema,
  tags: ['clarity', 'microsoft', 'heatmap', 'analytics', 'session'],
};
